import { useCallback, useEffect, useMemo, useState } from 'react';

import { csrClient } from '../../../apollo/api/avantArteClient';
import AvantArteAccountGateway from '../../../gateways/AvantArteAccountGateway';
import { AvantArtePaymentGateway } from '../../../gateways/AvantArtePaymentGateway';
import {
  GeneratedAddress,
  GeneratedPaymentMethod,
} from '../../../types/generated';
import { captureError } from '../../../utils/capture';
import { sortIso8601 } from '../../../utils/sort';

const accountGateway = new AvantArteAccountGateway(csrClient);
const paymentGateway = new AvantArtePaymentGateway(csrClient);

type UseCheckoutDetailsArgs = {
  enabled: boolean;
};

export type CheckoutDetailsController = {
  addresses: GeneratedAddress[];
  defaultAddressId?: string;
  defaultCardId?: string;
  loaded: boolean;
  paymentMethods: GeneratedPaymentMethod[];
  refreshAddresses: () => Promise<void>;
  refreshPaymentMethods: () => Promise<void>;
};

export const useCheckoutDetails = ({
  enabled,
}: UseCheckoutDetailsArgs): CheckoutDetailsController => {
  const [addresses, setAddresses] = useState<GeneratedAddress[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<
    GeneratedPaymentMethod[]
  >([]);
  const [loaded, setLoaded] = useState(false);

  const refreshAddresses = useCallback(async () => {
    const response = await accountGateway.getAddresses();
    if (Array.isArray(response)) setAddresses(response);
  }, []);

  const refreshPaymentMethods = useCallback(async () => {
    const response = await paymentGateway.listPaymentMethods();
    setPaymentMethods(Array.isArray(response) ? response : []);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    void (async () => {
      try {
        await Promise.all([refreshAddresses(), refreshPaymentMethods()]);
      } catch (error) {
        if (error instanceof Error) captureError(error);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, refreshAddresses, refreshPaymentMethods]);

  const defaultAddressId = useMemo(() => {
    const preferred = addresses.find((address) => address.isDefault);
    if (preferred) return preferred.id;

    return [...addresses].sort(
      sortIso8601(
        (address) =>
          address.updatedAt || address.createdAt || '2020-01-01T00:00:00Z',
      ),
    )[0]?.id;
  }, [addresses]);

  const defaultCardId = useMemo(() => {
    const preferred = paymentMethods.find((method) => method.default);
    return (preferred ?? paymentMethods[0])?.providerPaymentMethodId;
  }, [paymentMethods]);

  return {
    addresses: enabled ? addresses : [],
    defaultAddressId: enabled ? defaultAddressId : undefined,
    defaultCardId: enabled ? defaultCardId : undefined,
    loaded: enabled ? loaded : false,
    paymentMethods: enabled ? paymentMethods : [],
    refreshAddresses,
    refreshPaymentMethods,
  };
};
